import MojitoServices from 'mojito/services';
import { pickBy } from 'mojito/utils';

const APCA_PREFERENCE = MojitoServices.UserInfo.types.AUTOMATIC_PRICE_CHANGE_ACCEPTANCE_PREFERENCE;
const { HIGHER, LOWER } = MojitoServices.Betslip.types.PRICE_CHANGE_TYPE;
const { ALLOW_HIGHER, ALLOW_LOWER, NOT_ALLOW } = MojitoServices.Betslip.types.PRICE_CHANGE_POLICY;

/**
 * Contains utility functions related to betting settings.
 *
 * @class utils
 * @memberof Mojito.Modules.BettingSettings
 */

/**
 * Maps automatic price change acceptance settings object, which is how the BettingSettings module
 * represents the data, to an automatic price change acceptance preference, which is how the
 * UserInfo represents the same data.
 *
 * @param {object} apcaSettings - Settings object.
 * @param {boolean} apcaSettings.higher - Flag for automatic acceptance of higher price changes.
 * @param {boolean} apcaSettings.lower - Flag for automatic acceptance of lower price changes.
 *
 * @returns {Array<Mojito.Services.Betslip.types.PRICE_CHANGE_POLICY>} PriceChangePolicy.
 * @function Mojito.Modules.BettingSettings.utils#apcaSettingsToPriceChangePolicies
 */
export function apcaSettingsToPriceChangePolicies(apcaSettings) {
    const trueKeysStr = Object.keys(pickBy(apcaSettings)).sort().join('');
    return {
        [HIGHER + LOWER]: [ALLOW_HIGHER, ALLOW_LOWER],
        [HIGHER]: [ALLOW_HIGHER],
        [LOWER]: [ALLOW_LOWER],
        '': [NOT_ALLOW],
    }[trueKeysStr];
}

/**
 * Maps an automatic price change acceptance preference, which is how the UserInfo represents the
 * data, to an automatic price change acceptance settings object, which is how the BettingSettings
 * module represent the same state.
 *
 * @param {Mojito.Services.UserInfo.types.AUTOMATIC_PRICE_CHANGE_ACCEPTANCE_PREFERENCE} apcaPreference - The apca Preference.
 *
 * @returns {{ higher: boolean, lower: boolean }} The apcaSettings.
 * @function Mojito.Modules.BettingSettings.utils#apcaPreferenceToApcaSettings
 */
export function apcaPreferenceToApcaSettings(apcaPreference) {
    return {
        [APCA_PREFERENCE.ALL]: {
            [HIGHER]: true,
            [LOWER]: true,
        },
        [APCA_PREFERENCE.HIGHER]: {
            [HIGHER]: true,
            [LOWER]: false,
        },
        [APCA_PREFERENCE.LOWER]: {
            [HIGHER]: false,
            [LOWER]: true,
        },
        [APCA_PREFERENCE.NONE]: {
            [HIGHER]: false,
            [LOWER]: false,
        },
    }[apcaPreference];
}
